import React from 'react';
import { Button, useToast, TableRow, SortRule } from '@economic/taco';

import { useCurrentAgreement } from '@economic/react-app';
import Filters from './components/Filters';
import DeleteProductGroupDialog from './DeleteProductGroupDialog';
import ProductGroupsTable from './components/Table';
import { t, useLocalization } from '../../shared/components/l10n/l10n';
import View, { useViewFilter } from '../../shared/components/View/View';
import { ViewProps } from '../types';
import { CreateProductGroupDialog, EditProductGroupDialog } from '../../forms/Inventory/CreateOrEditProductGroup';
import { AccountingRules, ProductGroup, VatZone } from '../../../types/internal-api';
import internalApiClient from '../../shared/util/api/internalApiClient';
import Loader from '../../shared/components/Loader';
import { useCollectionQuery } from '../../shared/hooks/useCollectionQuery';
import { useTableSortPreferences } from '../../shared/hooks/useTableSortPreference';
import { useExcelExporter } from '../../shared/hooks/useExcelExporter';
import { Entity } from '../../../types';
import { getAPIErrorValidation, getErrorMessage } from '../../../utilities/parseAPIValidationErrors';
import useKeyShortcut from '../../shared/hooks/useKeyShortcut';
import { escapeSpecialChars } from '../../shared/util/utilities/escapeSpecialChars';
import useCachedQuery from '../../shared/hooks/useCachedQuery';
import useQuery from '../../shared/hooks/useQuery';

type ProductGroupsViewProps = Omit<ViewProps<ProductGroup>, 'onSelected'> & {
    compact?: boolean;
    onSelected?: (data: ProductGroup) => void;
};

const getQueryPath = (filter = '') => {
    const encodedFilter = encodeURIComponent(escapeSpecialChars(filter.trim()));
    const filterQuery = encodedFilter && `?filter=name$like:${encodedFilter}$or:id$like:${encodedFilter}`;
    return `/products/groups${filterQuery}`;
};

// Used by useExcelExporter to map column names with column ids as per old UI
const columnIndex = {
    id: 1,
    name: 2,
    'accountingRules.domesticAccountNumber': 3,
    'accountingRules.euAccountNumber': 4,
    'accountingRules.abroadAccountNumber': 5,
    'accountingRules.domesticWithoutVatAccountNumber': 6,
    'accountingRules.currentProjectsAccountNumber': 7,
    isServiceProducts: 8,
};

const ProductGroupsView = ({ compact = false, onCreated: handleCreated, onSelected: handleSelected }: ProductGroupsViewProps) => {
    const agreement = useCurrentAgreement();
    const currentAgreementNumber = agreement.number;
    // Allow different agreements to have different persisting data
    const key = `${currentAgreementNumber}/${compact ? 'productGroupsCompact' : 'productGroups'}`;

    const [filter, setFilter] = useViewFilter(!compact ? key : undefined);
    const tableSort = useTableSortPreferences(key);
    const [sortRules, setSortRules] = React.useState<SortRule<any>[] | undefined>(tableSort.sortRules);
    const productGroupsQuery = useCollectionQuery<ProductGroup>(getQueryPath(filter), compact);
    const countrySpecificVatZones = useCachedQuery<VatZone[]>('/vat/zones-country-specific', []).data;
    const showCountrySpecificFeatures = useQuery<boolean>('/vat/show-country-specific-vat-features', false).data;

    const toast = useToast();

    const exporter = useExcelExporter(Entity.ProductGroup, sortRules, columnIndex, filter);

    const [showCreateDialog, setShowCreateDialog] = React.useState(false);
    const [editingProductGroupNumber, setEditingProductGroupNumber] = React.useState<number | undefined>();
    const [copyingProductGroupNumber, setCopyingProductGroupNumber] = React.useState<number | undefined>();
    const [deletingProductGroupNumber, setDeletingProductGroupNumber] = React.useState<number | undefined>();

    useKeyShortcut('n', () => setShowCreateDialog(true));

    const handleRowDelete = (id: number) => async () => {
        try {
            await internalApiClient.delete(`/products/groups/${id}`);
            await productGroupsQuery.refetch();

            toast.success(t`TOAST_PRODUCT_GROUP_DELETE_SUCCESS`);
        } catch (error) {
            error.validation = getAPIErrorValidation(error);

            const message = getErrorMessage(error);

            toast.error(message);
        }
    };
    const handleProductGroupDialogSubmitted = async (productGroup: ProductGroup) => {
        await productGroupsQuery.refetch();
        handleCreated?.(productGroup);
    };
    // Saving sortRules so that it can be passed to useExcelExporter
    const handleSorting = (sortRules: SortRule<any>[] | undefined) => {
        tableSort.onSort(sortRules);
        setSortRules(sortRules);
    };

    const onRowClick = (row: TableRow<ProductGroup>) => {
        handleSelected?.(row.values);
    };
    const onRowEdit = (row: TableRow<ProductGroup>) => {
        setEditingProductGroupNumber(row.values.id);
    };
    const onRowCopy = (row: TableRow<ProductGroup>) => {
        setCopyingProductGroupNumber(row.values.id);
    };
    const onRowDelete = (row: TableRow<ProductGroup>) => {
        setDeletingProductGroupNumber(row.values.id);
    };

    const rowActions = { onRowEdit, onRowCopy, onRowDelete };
    const getProductGroupById = (id: number) => {
        const pGroup = productGroupsQuery.data.find(pg => pg.id === id);

        return appendCountrySpecificVatRules(pGroup);
    };

    const getEmptyProductGroup = () => {
        return appendCountrySpecificVatRules(undefined);
    };

    const appendCountrySpecificVatRules = (pGroup: Partial<ProductGroup> | undefined) => {
        if (!showCountrySpecificFeatures) {
            return pGroup;
        }

        if (!pGroup) {
            pGroup = {} as ProductGroup;
        }

        const countrySpecificVatRules = getCountrySpecificVatRules(pGroup);
        if (!pGroup.accountingRules) {
            pGroup.accountingRules = {} as AccountingRules;
        }
        pGroup.accountingRules.countrySpecificVatRuleItems = countrySpecificVatRules;

        return pGroup;
    };

    const getCountrySpecificVatRules = (productGroup: Partial<ProductGroup>) => {
        if (!countrySpecificVatZones || countrySpecificVatZones.length === 0) {
            return;
        }

        return countrySpecificVatZones.map(item => {
            const ruleItem = productGroup.accountingRules?.countrySpecificVatRuleItems?.find(p => p.zoneId === item.number);
            return {
                zoneId: item.number,
                name: item.name,
                accountNumber: ruleItem?.accountNumber!,
                textId: item.textId,
            };
        });
    };

    const createButton = (
        <Button appearance="primary" onClick={() => setShowCreateDialog(true)}>
            {t`FORM_GOODSGROUPS_CREATETEXT`}
        </Button>
    );

    return (
        <View>
            <View.Title>{t`FORM_GOODSGROUPS_HEADLINE`}</View.Title>
            <View.Header>
                <View.Header.Actions>{createButton}</View.Header.Actions>
                <View.Header.Filters>
                    <Filters filter={filter} onFilter={setFilter} compact={compact} exporter={exporter} />
                </View.Header.Filters>
            </View.Header>
            <View.Content>
                <ProductGroupsTable
                    loading={productGroupsQuery.loading}
                    data={productGroupsQuery.data}
                    showCountrySpecificFeatures={showCountrySpecificFeatures}
                    totalItems={productGroupsQuery.totalUnfilteredLength}
                    onRowClick={onRowClick}
                    rowActions={rowActions}
                    createDialog={dialogProps => (
                        <CreateProductGroupDialog {...dialogProps} onSubmitted={handleProductGroupDialogSubmitted} />
                    )}
                    sorting={{ sortRules, onSort: handleSorting }}
                    compact={compact}
                />
                {showCreateDialog && (
                    <CreateProductGroupDialog
                        initialData={getEmptyProductGroup()}
                        onClose={() => setShowCreateDialog(false)}
                        onSubmitted={handleProductGroupDialogSubmitted}
                        defaultOpen
                    />
                )}
                {editingProductGroupNumber && (
                    <EditProductGroupDialog
                        initialData={getProductGroupById(editingProductGroupNumber)}
                        onClose={() => setEditingProductGroupNumber(undefined)}
                        onSubmitted={() => productGroupsQuery.refetch()}
                        arrowKeySaveAndChangeHandler={nextItemId => setEditingProductGroupNumber(nextItemId as number)}
                        collectionData={productGroupsQuery.data}
                        idKeyProp="id"
                        currentItemId={editingProductGroupNumber}
                        defaultOpen
                    />
                )}
                {copyingProductGroupNumber && (
                    <EditProductGroupDialog
                        initialData={{
                            ...getProductGroupById(copyingProductGroupNumber),
                            id: undefined,
                        }}
                        onClose={() => setCopyingProductGroupNumber(undefined)}
                        onSubmitted={handleProductGroupDialogSubmitted}
                        defaultOpen
                    />
                )}
                {deletingProductGroupNumber && (
                    <DeleteProductGroupDialog
                        productGroupName={getProductGroupById(deletingProductGroupNumber)?.name}
                        onClose={() => setDeletingProductGroupNumber(undefined)}
                        onConfirm={handleRowDelete(deletingProductGroupNumber)}
                    />
                )}
            </View.Content>
        </View>
    );
};

export default function ProductGroupsWithLocalization(props: ProductGroupsViewProps) {
    const loadingl10n = useLocalization(textIds);

    if (loadingl10n) {
        return <Loader />;
    }

    return <ProductGroupsView {...props} />;
}

const textIds = [
    'FORM_GOODSGROUPS_CREATETEXT',
    'FORM_GOODSGROUPS_EMPTY_STATE',
    'SHARED_RECEIVABLESVATZONE_DENMARK',
    'SHARED_RECEIVABLESVATZONE_EU',
    'SHARED_RECEIVABLESVATZONE_OUTSIDEEU',
    'SHARED_RECEIVABLESVATZONE_DOMESTIC_NoVAT',
    'SHARED_RECEIVABLESVATZONE_PROJECTACCOUNT',
    'SHARED_RECEIVABLESVATZONE_HAS_COUNTRY_SPECIFIC_VAT',
    'FORM_GOODSGROUPS_COLHEADER_STOCKACCOUNT',
    'FIREBIRD_POPUP_TITLE_PLEASE_CONFIRM',
    'FORM_GOODSGROUPS_DELETEMESSAGE',
    'DIALOG_DELETE_PRODUCT_GROUP',
    'TOAST_PRODUCT_GROUP_DELETE_SUCCESS',
    'POPUPSEARCH_XRECORDSFOUND',
    'SHARED_YESORNO_NO',
    'SHARED_YESORNO_YES',
    'FORM_DELETEMESSAGE',
    'GOODSGROUPS_CREATETEXT',
];
